import {
  createEmailConfig,
  createEmailConfigForEventTemplate,
  partialUpdateEmail,
  updateEmailConfigForEventTemplate
} from '@goldcast/api/backend';
import logger from '@goldcast/logger';

import {
  AUDIT_LOG_PAGE_SIZE,
  CreditRuleRequestFor,
  type Rule,
  type AuditLogResponse,
  type CertificatesUsedResponse,
  type FetchAuditLogParams,
  type FetchCertificatesUsedParams,
  type EmailDesignPreview,
  type ResendCertificateParams,
  type ResendCertificateResponse,
  type RuleSetFormData
} from '../types';
import {
  type CopyRuleSetRequest,
  type CopyRuleSet,
  type RuleSetResponse,
  ApiEntityType,
  copyRuleSetPath,
  entityTypeForRequestFor,
  fromRuleSetResponse,
  rulesetRulePath,
  toPatchRuleRequest,
  toRuleSetRequest
} from './rulesetApi';

import type { ActionContext } from 'vuex';

import { getEnv } from '@/utils/env';

export interface ProfessionalEducationState {
  creditRuleData: RuleSetFormData;
  hasRuleConfigured: boolean;
  configuredFor: string;
  configuredRequestFor: CreditRuleRequestFor;
  ruleSetId: string;
  ruleIds: string[];
  resolvedEmailTemplateId: string;
  loading: boolean;
  saving: boolean;
}

type Context = ActionContext<ProfessionalEducationState, any>;

interface EntityPayload {
  id: string;
  requestFor: CreditRuleRequestFor;
}

interface ApiError extends Error {
  statusCode?: number;
}

interface EmailConfigPart {
  id?: string;
  name?: string;
  email_html?: string;
  html_body?: string;
}

interface EventEmailConfigResponse {
  id?: string;
  subject?: string;
  use_org_template?: boolean;
  organization_template?: EmailConfigPart | null;
  event_email_template?: EmailConfigPart | null;
}

interface StudioTemplateEmailConfigResponse {
  id?: string;
  subject?: string;
  email_template?: EmailConfigPart | null;
}

function createEmptyFormData(): RuleSetFormData {
  return { rules: [], certificate: '', emailDesign: '' };
}

const baseUrl = getEnv('VUE_APP_CPE_API_URL');

const state = (): ProfessionalEducationState => ({
  creditRuleData: createEmptyFormData(),
  hasRuleConfigured: false,
  configuredFor: '',
  configuredRequestFor: CreditRuleRequestFor.None,
  ruleSetId: '',
  ruleIds: [],
  resolvedEmailTemplateId: '',
  loading: false,
  saving: false
});

const mutations = {
  setCreditRuleData(
    state: ProfessionalEducationState,
    payload: { id: string; requestFor: CreditRuleRequestFor; data: RuleSetFormData }
  ) {
    state.creditRuleData = payload.data;
    state.hasRuleConfigured = true;
    state.configuredFor = payload.id;
    state.configuredRequestFor = payload.requestFor;
    state.resolvedEmailTemplateId = '';
  },
  setRuleSetId(state: ProfessionalEducationState, ruleSetId: string) {
    state.ruleSetId = ruleSetId;
  },
  setRuleIds(state: ProfessionalEducationState, ruleIds: string[]) {
    state.ruleIds = ruleIds;
  },
  updateRuleAtIndex(state: ProfessionalEducationState, { ruleIndex, rule }: { ruleIndex: number; rule: Rule }) {
    if (state.creditRuleData.rules[ruleIndex] !== undefined) {
      state.creditRuleData.rules[ruleIndex] = rule;
    }
  },
  setLoading(state: ProfessionalEducationState, loading: boolean) {
    state.loading = loading;
  },
  setSaving(state: ProfessionalEducationState, saving: boolean) {
    state.saving = saving;
  },
  setResolvedEmailTemplateId(state: ProfessionalEducationState, id: string) {
    state.resolvedEmailTemplateId = id;
  },
  clearCreditRuleData(state: ProfessionalEducationState) {
    state.creditRuleData = createEmptyFormData();
    state.hasRuleConfigured = false;
    state.configuredFor = '';
    state.configuredRequestFor = CreditRuleRequestFor.None;
    state.ruleSetId = '';
    state.ruleIds = [];
    state.resolvedEmailTemplateId = '';
  }
};

const getters = {
  getCreditRuleDataFor:
    (state: ProfessionalEducationState) =>
    (id: string, requestFor: CreditRuleRequestFor): RuleSetFormData | null => {
      if (state.configuredFor === id && state.configuredRequestFor === requestFor) {
        return state.creditRuleData;
      }
      return null;
    },
  isConfiguredFor:
    (state: ProfessionalEducationState) =>
    (id: string, requestFor: CreditRuleRequestFor): boolean => {
      return state.hasRuleConfigured && state.configuredFor === id && state.configuredRequestFor === requestFor;
    }
};

const actions = {
  async fetchCreditRuleData(context: Context, { id, requestFor }: EntityPayload): Promise<void> {
    if (!id || requestFor === CreditRuleRequestFor.None) {
      return;
    }
    context.commit('setLoading', true);
    const query = `entity_type=${entityTypeForRequestFor(requestFor)}&entity_id=${encodeURIComponent(id)}`;
    try {
      const response: RuleSetResponse[] | RuleSetResponse | null = await context.dispatch(
        'makeapicall',
        {
          completeURL: `${baseUrl}/api/v1/cpe/rulesets?${query}`,
          method: 'GET'
        },
        { root: true }
      );
      const ruleSets: RuleSetResponse[] = Array.isArray(response) ? response : response ? [response] : [];
      if (ruleSets.length === 0) {
        context.commit('clearCreditRuleData');
        return;
      }
      const ruleSet = ruleSets[0];
      context.commit('setCreditRuleData', { id, requestFor, data: fromRuleSetResponse(ruleSet) });
      context.commit('setRuleSetId', ruleSet.id);
      context.commit(
        'setRuleIds',
        (ruleSet.rules ?? []).map(r => r.id)
      );
    } catch (error) {
      // Backend intentionally returns 404 when no ruleset exists for the entity.
      if ((error as ApiError)?.statusCode === 404) {
        context.commit('clearCreditRuleData');
        return;
      }
      context.dispatch(
        'studioToast/triggerToast',
        { msg: 'Failed to load professional education credits.', mode: 'error' },
        { root: true }
      );
      throw error;
    } finally {
      context.commit('setLoading', false);
    }
  },

  async persistCreditRuleData(context: Context): Promise<boolean> {
    const { creditRuleData, configuredFor, configuredRequestFor, ruleSetId } = context.state;
    const request = toRuleSetRequest(configuredFor, configuredRequestFor, creditRuleData);
    context.commit('setSaving', true);

    let response: RuleSetResponse;
    try {
      response = await context.dispatch(
        'makeapicall',
        ruleSetId
          ? {
              completeURL: `${baseUrl}/api/v1/cpe/rulesets/${ruleSetId}`,
              method: 'PUT',
              body: JSON.stringify(request)
            }
          : {
              completeURL: `${baseUrl}/api/v1/cpe/rulesets`,
              method: 'POST',
              body: JSON.stringify(request)
            },
        { root: true }
      );
    } catch (error) {
      context.dispatch(
        'studioToast/triggerToast',
        { msg: 'Failed to save professional education credits.', mode: 'error' },
        { root: true }
      );
      context.commit('setSaving', false);
      return false;
    }

    context.commit('setRuleSetId', response.id);
    context.commit(
      'setRuleIds',
      (response.rules ?? []).map(r => r.id)
    );

    // Scope each rule via PATCH — distribution_level lives per-rule, aligned by position.
    const validFormRules = creditRuleData.rules;
    try {
      await Promise.all(
        (response.rules ?? []).map((apiRule, i) => {
          const formRule = validFormRules[i];
          if (!formRule) {
            return Promise.resolve();
          }
          // Only PATCH if the user has explicitly chosen a distribution level.
          const patchRequest = toPatchRuleRequest(formRule);
          if (!patchRequest) {
            return Promise.resolve();
          }
          return context.dispatch(
            'makeapicall',
            {
              completeURL: `${baseUrl}${rulesetRulePath(response.id, apiRule.id)}`,
              method: 'PATCH',
              body: JSON.stringify(patchRequest)
            },
            { root: true }
          );
        })
      );
    } catch {
      // The ruleset was saved; only distribution scoping failed.
      context.dispatch(
        'studioToast/triggerToast',
        { msg: 'Rule distribution could not be saved; please retry.', mode: 'warn' },
        { root: true }
      );
    } finally {
      context.commit('setSaving', false);
    }

    return true;
  },

  async createEventEmail(
    context: Context,
    { eventId, emailDesign, emailSubject }: { eventId: string; emailDesign: string; emailSubject: string }
  ): Promise<void> {
    const response = await createEmailConfig({
      body: {
        event: eventId,
        email_type: 'CPECertificate',
        subject: emailSubject,
        organization_template: emailDesign,
        active: true
      }
    });
    if (response.id) {
      context.commit('setResolvedEmailTemplateId', response.id);
    }
  },

  async createTemplateEmail(
    context: Context,
    { templateId, emailDesign, emailSubject }: { templateId: string; emailDesign: string; emailSubject: string }
  ): Promise<void> {
    const response = await createEmailConfigForEventTemplate({
      body: {
        email_type: 'CPECertificate',
        subject: emailSubject,
        active: true,
        email_template: emailDesign,
        event_template: templateId
      }
    });
    if (response.id) {
      context.commit('setResolvedEmailTemplateId', response.id);
    }
  },

  async updateEventEmail(
    _context: Context,
    { configId, emailDesign, emailSubject }: { configId: string; emailDesign: string; emailSubject: string }
  ): Promise<void> {
    await partialUpdateEmail({
      id: configId,
      body: {
        active: true,
        subject: emailSubject,
        organization_template: emailDesign
      }
    });
  },

  async updateTemplateEmail(
    _context: Context,
    { configId, emailDesign, emailSubject }: { configId: string; emailDesign: string; emailSubject: string }
  ): Promise<void> {
    await updateEmailConfigForEventTemplate({
      id: configId,
      body: {
        active: true,
        subject: emailSubject,
        email_template: emailDesign
      }
    });
  },

  async fetchEmailDesignPreview(
    context: Context,
    { id, requestFor }: EntityPayload
  ): Promise<EmailDesignPreview | null> {
    if (!id || requestFor === CreditRuleRequestFor.None) {
      return null;
    }
    const url =
      requestFor === CreditRuleRequestFor.Template
        ? `/studio_template/studio_email_config_template/?event_template=${encodeURIComponent(id)}&email_type=CPECertificate`
        : `/event/email/?event=${encodeURIComponent(id)}&email_type=CPECertificate`;

    const response: unknown = await context.dispatch('makeapicall', { url, method: 'GET' }, { root: true });
    const config = Array.isArray(response) ? response[0] : response;
    if (!config) {
      return null;
    }

    if (requestFor === CreditRuleRequestFor.Template) {
      const cfg = config as StudioTemplateEmailConfigResponse;
      if (cfg.id) {
        context.commit('setResolvedEmailTemplateId', cfg.id);
      }
      const part = cfg.email_template;
      if (!part) {
        return null;
      }
      return {
        id: cfg.id ?? '',
        designId: part.id ?? '',
        name: part.name ?? '',
        email_html: part.email_html ?? part.html_body ?? '',
        subject: cfg.subject ?? ''
      };
    }

    const eventConfig = config as EventEmailConfigResponse;
    const part = eventConfig.use_org_template ? eventConfig.organization_template : eventConfig.event_email_template;
    if (!part) {
      return null;
    }
    if (eventConfig.id) {
      context.commit('setResolvedEmailTemplateId', eventConfig.id);
    }
    return {
      id: eventConfig.id ?? '',
      designId: part.id ?? '',
      name: part.name ?? '',
      email_html: part.email_html ?? part.html_body ?? '',
      subject: eventConfig.subject ?? ''
    };
  },

  async saveCreditRuleData(
    context: Context,
    {
      id,
      requestFor,
      data,
      isWebinar
    }: { id: string; requestFor: CreditRuleRequestFor; data: RuleSetFormData; isWebinar?: boolean }
  ): Promise<boolean> {
    const normalizedData = isWebinar ? { ...data, rules: data.rules.map(r => ({ ...r, appliesToEvent: true })) } : data;
    context.commit('setCreditRuleData', { id, requestFor, data: normalizedData });
    return context.dispatch('persistCreditRuleData');
  },

  async patchRuleAtIndex(
    context: Context,
    { ruleIndex, updatedRule }: { ruleIndex: number; updatedRule: Rule }
  ): Promise<void> {
    const { ruleSetId, ruleIds } = context.state;
    const ruleId = ruleIds[ruleIndex];
    if (!ruleSetId || !ruleId) {
      return;
    }
    const patchRequest = toPatchRuleRequest(updatedRule);
    if (!patchRequest) {
      return;
    }
    const originalRule = context.state.creditRuleData.rules[ruleIndex];
    context.commit('updateRuleAtIndex', { ruleIndex, rule: updatedRule });
    context.commit('setSaving', true);
    try {
      await context.dispatch(
        'makeapicall',
        {
          completeURL: `${baseUrl}${rulesetRulePath(ruleSetId, ruleId)}`,
          method: 'PATCH',
          body: JSON.stringify(patchRequest)
        },
        { root: true }
      );
    } catch {
      context.commit('updateRuleAtIndex', { ruleIndex, rule: originalRule });
      context.dispatch(
        'studioToast/triggerToast',
        { msg: 'Failed to update rule distribution.', mode: 'error' },
        { root: true }
      );
    } finally {
      context.commit('setSaving', false);
    }
  },
  async copyRuleSetBetweenEntities(
    context: Context,
    {
      sourceEntityType,
      sourceEntityId,
      targetEntityType,
      targetEntityId,
      patchDistributionPEREvent
    }: {
      patchDistributionPEREvent?: boolean;
      sourceEntityType: ApiEntityType;
      sourceEntityId: string;
      targetEntityType: ApiEntityType;
      targetEntityId: string;
    }
  ): Promise<void> {
    let ruleSets: RuleSetResponse[] | undefined;
    try {
      ruleSets = await context.dispatch(
        'makeapicall',
        {
          completeURL: `${baseUrl}/api/v1/cpe/rulesets/copy`,
          method: 'POST',
          body: JSON.stringify({
            source_entity_type: sourceEntityType,
            source_entity_id: sourceEntityId,
            target_entity_type: targetEntityType,
            target_entity_id: targetEntityId
          } as CopyRuleSet)
        },
        { root: true }
      );
    } catch (error) {
      // 404 means the template has no CPE ruleset — nothing to copy.
      if ((error as ApiError)?.statusCode === 404) {
        return;
      }
      context.dispatch(
        'studioToast/triggerToast',
        { msg: 'Error copying professional education credits.', mode: 'error' },
        { root: true }
      );
      return;
    }

    if (!patchDistributionPEREvent || !ruleSets?.length) {
      return;
    }

    try {
      await Promise.all(
        ruleSets.flatMap(ruleSet =>
          (ruleSet.rules ?? []).map(rule =>
            context.dispatch(
              'makeapicall',
              {
                completeURL: `${baseUrl}${rulesetRulePath(ruleSet.id, rule.id)}`,
                method: 'PATCH',
                body: JSON.stringify({ distribution_level: 'PER_EVENT' })
              },
              { root: true }
            )
          )
        )
      );
    } catch {
      context.dispatch(
        'studioToast/triggerToast',
        { msg: 'Rule distribution could not be saved; please retry.', mode: 'warn' },
        { root: true }
      );
    }
  },
  async copyRuleSetFromTemplate(
    context: Context,
    {
      eventId,
      templateId,
      patchDistributionPEREvent
    }: { eventId: string; templateId: string; patchDistributionPEREvent?: boolean }
  ): Promise<void> {
    let ruleSet: RuleSetResponse | undefined;
    try {
      ruleSet = await context.dispatch(
        'makeapicall',
        {
          completeURL: `${baseUrl}${copyRuleSetPath(eventId)}`,
          method: 'POST',
          body: JSON.stringify({ template_id: templateId } as CopyRuleSetRequest)
        },
        { root: true }
      );
    } catch (error) {
      // 404 = template has no CPE ruleset; 400 = template has no rulesets configured — nothing to copy, no toast.
      if ((error as ApiError)?.statusCode === 404 || (error as ApiError)?.statusCode === 400) {
        return;
      }
      logger.error('[store/index] copyRuleSetFromTemplate failed', error);
      context.dispatch(
        'studioToast/triggerToast',
        { msg: 'Professional education credits could not be copied from the template.', mode: 'error' },
        { root: true }
      );
      return;
    }

    if (!patchDistributionPEREvent || !ruleSet) {
      return;
    }

    try {
      await Promise.all(
        (ruleSet.rules ?? []).map(rule =>
          context.dispatch(
            'makeapicall',
            {
              completeURL: `${baseUrl}${rulesetRulePath(ruleSet.id, rule.id)}`,
              method: 'PATCH',
              body: JSON.stringify({ distribution_level: 'PER_EVENT' })
            },
            { root: true }
          )
        )
      );
    } catch {
      context.dispatch(
        'studioToast/triggerToast',
        { msg: 'Rule distribution could not be saved; please retry.', mode: 'warn' },
        { root: true }
      );
    }
  },

  async removeCreditRuleData(context: Context): Promise<void> {
    const existingId = context.state.ruleSetId;
    if (existingId) {
      try {
        await context.dispatch(
          'makeapicall',
          {
            completeURL: `${baseUrl}/api/v1/cpe/rulesets/${existingId}`,
            method: 'DELETE'
          },
          { root: true }
        );
      } catch (error) {
        context.dispatch(
          'studioToast/triggerToast',
          { msg: 'Failed to remove professional education credits.', mode: 'error' },
          { root: true }
        );
        return;
      }
    }
    context.commit('clearCreditRuleData');
  },
  async fetchCertificatesUsed(
    { dispatch },
    { orgId, timeRange }: FetchCertificatesUsedParams
  ): Promise<CertificatesUsedResponse> {
    const query = new URLSearchParams({ org_id: orgId });
    if (timeRange) {
      query.set('time_range', timeRange);
    }
    return dispatch(
      'makeapicall',
      { method: 'GET', completeURL: `${baseUrl}/internal/certificates-used?${query.toString()}` },
      { root: true }
    );
  },
  async resendCertificate({ dispatch }, params: ResendCertificateParams): Promise<ResendCertificateResponse> {
    const completeURL = `${baseUrl}/api/v1/cpe/certificates/resend`;
    return dispatch('makeapicall', { method: 'POST', completeURL, body: JSON.stringify(params) }, { root: true });
  },

  async fetchAuditLog({ dispatch }, params: FetchAuditLogParams = {}): Promise<AuditLogResponse> {
    const query = new URLSearchParams();

    if (params.eventId) {
      query.set('event_id', params.eventId);
    }
    if (params.sessionId) {
      query.set('session_id', params.sessionId);
    }
    if (params.scope) {
      query.set('scope', params.scope);
    }
    if (params.qualified === false) {
      query.set('qualified', 'false');
    }
    if (params.email) {
      query.set('email', params.email);
    }
    query.set('page', String(params.page ?? 1));
    query.set('page_size', String(params.pageSize ?? AUDIT_LOG_PAGE_SIZE));

    const completeURL = `${baseUrl}/api/v1/cpe/audit-log?${query.toString()}`;

    return dispatch('makeapicall', { method: 'GET', completeURL }, { root: true });
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
